import { useMemo, useState } from 'react'

type Selector<T> = (item: T) => number
type UseSelectableOptions<T> = {
    getId: Selector<T>
    isSelectable?: (item: T) => boolean
}

export function useSelectable<T>(items: T[], options: UseSelectableOptions<T>) {
    const { getId, isSelectable = () => true } = options
    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())

    const applicableItems = useMemo(() => items.filter(isSelectable), [items, isSelectable])

    const toggle = (item: T) => {
        const id = getId(item)
        setSelectedIds((prev) => {
            const next = new Set(prev)
            next.has(id) ? next.delete(id) : next.add(id)
            return next
        })
    }

    const toggleAll = () => {
        if (selectedIds.size === applicableItems.length) {
            setSelectedIds(new Set())
        } else {
            setSelectedIds(new Set(applicableItems.map(getId)))
        }
    }

    const isChecked = (item: T) => {
        return selectedIds.has(getId(item))
    }

    const isAllChecked = selectedIds.size === applicableItems.length
    const isNonChecked = selectedIds.size === 0

    return {
        selected: Array.from(selectedIds),
        toggle,
        toggleAll,
        isChecked,
        isAllChecked,
        isNonChecked,
        reset: () => setSelectedIds(new Set()),
    }
}
